
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/auth/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Search, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const MentorStudents = () => {
  const { isMentor } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  
  // Check permissions
  useEffect(() => {
    if (!isMentor()) {
      navigate('/dashboard');
    }
  }, [isMentor, navigate]);

  // Fetch students with their progress
  const { data: studentsWithProgress, isLoading } = useQuery({
    queryKey: ['mentor-students-progress'],
    queryFn: async () => {
      try {
        // First, get all student users
        const { data: userRoles, error: rolesError } = await supabase
          .from('user_roles')
          .select('user_id')
          .eq('role', 'student');
        
        if (rolesError) throw rolesError;
        
        if (!userRoles || userRoles.length === 0) {
          return [];
        }
        
        const studentIds = userRoles.map(role => role.user_id);
        
        // Get user profiles
        const { data: profiles, error: profilesError } = await supabase
          .from('user_profiles')
          .select('id, first_name, last_name, email, profile_photo_url')
          .in('id', studentIds);
        
        if (profilesError) throw profilesError;
        
        // Get modules count (for overall progress calculation)
        const { count: modulesCount, error: modulesError } = await supabase
          .from('learning_modules')
          .select('*', { count: 'exact', head: true });
        
        if (modulesError) throw modulesError;
        
        // Get progress for each student
        const studentsWithProgressData = await Promise.all(
          (profiles || []).map(async (profile) => {
            // Get user progress records
            const { data: progressData, error: progressError } = await supabase
              .from('user_progress')
              .select('module_id, completion_percentage, completed_at')
              .eq('user_id', profile.id);
            
            if (progressError) {
              console.error('Error fetching progress for user:', profile.id, progressError);
              return {
                ...profile,
                totalProgress: 0,
                completedModules: 0,
                inProgressModules: 0
              };
            }
            
            // Calculate total progress
            const completedModules = (progressData || []).filter(p => p.completion_percentage === 100).length;
            const inProgressModules = (progressData || []).filter(p => p.completion_percentage > 0 && p.completion_percentage < 100).length;
            const totalProgress = modulesCount ? Math.round((completedModules / modulesCount) * 100) : 0;
            
            return {
              ...profile,
              totalProgress,
              completedModules,
              inProgressModules,
              progress: progressData || []
            };
          })
        );
        
        return studentsWithProgressData;
      } catch (error) {
        console.error('Error loading students progress:', error);
        toast.error('Error loading students data');
        return [];
      }
    }
  });

  const filteredStudents = studentsWithProgress?.filter(student => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      (student.first_name?.toLowerCase() || '').includes(searchLower) ||
      (student.last_name?.toLowerCase() || '').includes(searchLower) ||
      (student.email?.toLowerCase() || '').includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold section-header">Students Progress</h1>
        <p className="text-muted-foreground section-subheader">Monitor your students' learning journey</p>
      </div>
      
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search students by name or email..."
          className="pl-8"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-full bg-muted"></div>
                  <div className="h-5 w-1/3 bg-muted rounded"></div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="h-2 w-full bg-muted rounded"></div>
                  <div className="h-4 w-1/4 bg-muted rounded"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredStudents && filteredStudents.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2">
          {filteredStudents.map((student) => (
            <Card key={student.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center space-x-3">
                  <Avatar>
                    <AvatarImage src={student.profile_photo_url || ''} alt={`${student.first_name} ${student.last_name}`} />
                    <AvatarFallback>{student.first_name?.[0]}{student.last_name?.[0]}</AvatarFallback>
                  </Avatar>
                  <CardTitle className="text-base font-medium">
                    {student.first_name} {student.last_name}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Overall Progress</span>
                    <span className="font-medium">{student.totalProgress}%</span>
                  </div>
                  <Progress value={student.totalProgress} className="h-2" />
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <Card className="p-2 bg-muted/50">
                    <div className="text-center">
                      <div className="text-xl font-bold">{student.completedModules}</div>
                      <div className="text-muted-foreground text-xs">Completed</div>
                    </div>
                  </Card>
                  <Card className="p-2 bg-muted/50">
                    <div className="text-center">
                      <div className="text-xl font-bold">{student.inProgressModules}</div>
                      <div className="text-muted-foreground text-xs">In Progress</div>
                    </div>
                  </Card>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-6">
            <div className="text-center">
              <User className="h-12 w-12 mx-auto text-muted-foreground" />
              <h3 className="mt-2 text-lg font-medium">No students found</h3>
              <p className="text-muted-foreground mt-1">
                {searchTerm 
                  ? "No students match your search criteria." 
                  : "There are no students in the system yet."}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MentorStudents;
